import { defineStore } from 'pinia'
import api from '@/api/tree'
import { deleteCache } from '@/utils/request'
import { arrayToObjects } from '@/utils'

export const useTreeStore = defineStore('tree', {
  state: () => ({
    clients: {} as Record<string, any>,
    industries: {} as Record<string, any>,
    organisations: {} as Record<string, any>,
  }),

  getters: {
    industriesGetter: (state) => state.industries,
    organisationsGetter: (state) => state.organisations,
    clientsGetter: (state) => state.clients,
    getClientDetail: (state) => (id) => {
      if (!id || !state.clients) return {}
      return state.clients[id]
    },
    // Display name for a client/TMC (TAMK3-25): the `name_alias` setting if
    // the TMC has set one, otherwise the import-matched `name`.
    clientDisplayName: (state) => (id) => {
      const client = id && state.clients ? state.clients[id] : null
      if (!client) return ''
      return client.settings?.name_alias || client.name || ''
    },
  },

  actions: {
    getClientsDetail (id?) {
      return api.getClientsDetail()
        .then(resp => { this.clients = arrayToObjects(resp.data || resp) })
    },

    // Default: non-archived only — that's what dashboard
    // filter dropdowns + the hierarchy picker callers want.
    // The Customer Names settings tab passes
    // `{includeArchived: true}` so it can render the archive
    // toggle for archived rows too.
    getOrganisations ({includeArchived = false} = {}) {
      return api.getOrganisationList({includeArchived})
        .then(data => { this.organisations = arrayToObjects(data) })
    },

    updateOrganisation (data) {
      return api.postOrganisationDetail(data)
        .then(() => { this.organisations[data.id] = {...this.organisations[data.id], ...data} })
    },

    // One-way merge — see backend tree/cleaners.py for full
    // mechanics. After the merge, every dashboard FK has been
    // re-pointed at the winner, the loser entity subtrees are
    // re-parented under the winner's root, and the loser org
    // rows are deleted. We refresh the in-memory orgs map so
    // the table re-renders without a full re-fetch, and bust
    // the hierarchy cache so the navbar picker reflects the
    // tree changes.
    mergeOrganisations ({organisations, primaryOrganisation, overrideName, overrideCode}) {
      return api.postOrganisationMerge({
        organisations,
        primaryOrganisation,
        overrideName,
        overrideCode,
      }).then(resp => {
        const body = resp?.data ?? resp
        const winnerId = body.merged_into ?? primaryOrganisation
        // Drop the loser rows from the in-memory map.
        for (const id of organisations) {
          if (id !== winnerId && this.organisations[id]) {
            delete this.organisations[id]
          }
        }
        // Optimistically apply the name/code override locally
        // so the table doesn't briefly flash with the old
        // values before the next refetch.
        if (this.organisations[winnerId]) {
          if (overrideName) {
            this.organisations[winnerId] = {
              ...this.organisations[winnerId],
              name: overrideName,
            }
          }
          if (overrideCode) {
            this.organisations[winnerId] = {
              ...this.organisations[winnerId],
              code: overrideCode,
            }
          }
        }
        // Tree shape has changed (loser roots reparented under
        // winner); bust the hierarchy cache so the navbar
        // picker reflects it.
        deleteCache('/api/tree/')
        return body
      })
    },

    setOrganisationArchived (id, isArchived) {
      return api.postOrganisationArchive(id, isArchived)
        .then(resp => {
          // `request.post` returns the raw AxiosResponse; the
          // backend's `{id, is_archived}` body lives at `.data`.
          const body = resp?.data ?? resp
          // Backend echoes the resulting state. Sync the store
          // row so the table re-renders without a full re-fetch.
          if (this.organisations[id]) {
            this.organisations[id] = {
              ...this.organisations[id],
              is_archived: body.is_archived,
            }
          }
          // Bust the frontend cache for every hierarchy-feeding
          // endpoint so the navbar picker refreshes the next
          // time it loads — without this, the cached pre-archive
          // response continues to serve until a hard page reload.
          // Covers all three: /api/tree/ (get_hierarchy),
          // /api/tree/flat/?top (NodeList top mode), and
          // /api/tree/node/<id>/ (node_detail per-node lazy load).
          deleteCache('/api/tree/')
          return body
        })
    },

    getIndustries () {
      return api.getIndustryList()
        .then(data => { this.industries = arrayToObjects(data) })
    },

    // Mutations exposed as actions
    // Called from settings.js clientReportLogoUploaded
    clientChanged (data) {
      this.clients[data.id] = {...this.clients[data.id], ...data}
    },
  }
})
